import { useEffect, useRef, useState } from "react";

/**
 * Cash App-style animated amount display.
 *
 * Scenarios:
 *   A — Zero replacement  ($0 → $N)   : crossfade via @keyframes (no width change)
 *   B — Digit append      ($1 → $12)  : CSS transition with max-width for recentering
 *   C — Digit delete      ($12 → $1)  : CSS transition with max-width for recentering
 *   D — Delete to zero    ($1 → $0)   : crossfade via @keyframes (no width change)
 */

// ---------------------------------------------------------------------------
// Timing constants (ms)
// ---------------------------------------------------------------------------
const EXIT_REPLACE = 160;
const ENTER_REPLACE = 260;
const ENTER_APPEND = 280;
const EXIT_DELETE = 140;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function classify(prev, next) {
  if (prev === next) return null;
  const prevIsZero = prev === "$0" || prev === "";
  const nextIsZero = next === "$0" || next === "";
  if (prevIsZero && !nextIsZero) return "A";
  if (!prevIsZero && nextIsZero) return "D";
  if (next.length > prev.length) return "B";
  if (next.length < prev.length) return "C";
  return "A";
}

function charsArray(str) {
  if (!str) return ["$", "0"];
  const chars = str.startsWith("$") ? str.split("") : ["$", ...str.split("")];
  return chars.length > 1 ? chars : ["$", "0"];
}

// All characters animate to the same max-width so enter-append looks uniform
function charWidth() {
  return "1ch";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export default function AnimatedAmountDisplay({ value, rejectBump = 0, showCaret = false }) {
  const masked = value?.masked || "";
  const display = masked || "$0";

  const prevRef = useRef(display);
  const [chars, setChars] = useState(() => charsArray(display));
  const [animating, setAnimating] = useState(new Map());
  const [exitOverlay, setExitOverlay] = useState(null);
  // For Scenario B: tracks indices that need a rAF to flip from collapsed → expanded
  const [collapsed, setCollapsed] = useState(new Set());
  const containerRef = useRef(null);
  const lastShakeRef = useRef(0);
  const timeoutsRef = useRef([]);
  const rafRef = useRef(null);

  useEffect(() => {
    return () => {
      timeoutsRef.current.forEach(clearTimeout);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Scenario E: shake when max digits reached or delete past zero
  useEffect(() => {
    if (rejectBump === 0) return;
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    setAnimating(new Map());
    setExitOverlay(null);
    setCollapsed(new Set());

    const el = containerRef.current;
    if (!el) return;

    const now = Date.now();
    const isExaggerated = now - lastShakeRef.current < 3000;
    lastShakeRef.current = now;

    // Force reflow to guarantee animation restart on every keypress
    el.classList.remove("aad-shake", "aad-shake-hard");
    void el.offsetWidth;
    el.classList.add(isExaggerated ? "aad-shake-hard" : "aad-shake");

    const duration = 575;
    const t = setTimeout(() => {
      el.classList.remove("aad-shake", "aad-shake-hard");
    }, duration);
    timeoutsRef.current.push(t);
  }, [rejectBump]);

  useEffect(() => {
    const prev = prevRef.current;
    const next = display;
    prevRef.current = next;

    const scenario = classify(prev, next);
    if (!scenario) return;

    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
    if (rafRef.current) cancelAnimationFrame(rafRef.current);

    const nextChars = charsArray(next);
    const prevChars = charsArray(prev);
    const anims = new Map();

    switch (scenario) {
      case "A":
      case "D": {
        const replaceIndex = Math.max(prevChars.length, nextChars.length) - 1;
        const exitChar = prevChars[prevChars.length - 1];

        anims.set(replaceIndex, {
          type: "enter-replace",
          duration: ENTER_REPLACE,
        });

        setChars(nextChars);
        setAnimating(new Map(anims));
        setExitOverlay({ index: replaceIndex, char: exitChar });
        setCollapsed(new Set());

        const t = setTimeout(() => {
          setAnimating(new Map());
          setExitOverlay(null);
        }, ENTER_REPLACE);
        timeoutsRef.current.push(t);
        break;
      }

      case "B": {
        // Two-pointer semantic diff: match prev chars into next chars.
        // Any next char without a match is "new" (inserted comma or appended digit).
        const newIndices = new Set();
        let pi = 0;
        for (let ni = 0; ni < nextChars.length; ni++) {
          if (pi < prevChars.length && nextChars[ni] === prevChars[pi]) {
            pi++;
          } else {
            anims.set(ni, { type: "enter-append", duration: ENTER_APPEND });
            newIndices.add(ni);
          }
        }

        setChars(nextChars);
        setAnimating(new Map(anims));
        setExitOverlay(null);
        // Start collapsed — next rAF will expand them to trigger the transition
        setCollapsed(new Set(newIndices));

        rafRef.current = requestAnimationFrame(() => {
          rafRef.current = requestAnimationFrame(() => {
            setCollapsed(new Set());
          });
        });

        const t = setTimeout(() => setAnimating(new Map()), ENTER_APPEND);
        timeoutsRef.current.push(t);
        break;
      }

      case "C": {
        // Two-pointer semantic diff: match next chars into prev chars.
        // Any prev char without a match is being removed (deleted digit or removed comma).
        let ni = 0;
        for (let pi = 0; pi < prevChars.length; pi++) {
          if (ni < nextChars.length && prevChars[pi] === nextChars[ni]) {
            ni++;
          } else {
            anims.set(pi, { type: "exit-delete", duration: EXIT_DELETE });
          }
        }

        setChars(prevChars);
        setAnimating(new Map(anims));
        setExitOverlay(null);
        setCollapsed(new Set());

        const t = setTimeout(() => {
          setChars(nextChars);
          setAnimating(new Map());
          setCollapsed(new Set());
        }, EXIT_DELETE);
        timeoutsRef.current.push(t);
        break;
      }

      default:
        setChars(nextChars);
        setAnimating(new Map());
        setExitOverlay(null);
        setCollapsed(new Set());
    }
  }, [display]);

  return (
    <div ref={containerRef} className="aad-container">
      {chars.map((char, i) => {
        const anim = animating.get(i);
        const isDollar = i === 0 && char === "$";
        const hasOverlay = exitOverlay?.index === i;
        const isCollapsed = collapsed.has(i);

        let className = "aad-char";
        if (isDollar) className += " aad-dollar";

        let style = {};

        if (anim && !isDollar) {
          switch (anim.type) {
            case "enter-replace":
              style = {
                animation: `aadEnterReplace ${anim.duration}ms linear forwards`,
              };
              break;

            case "enter-append":
              className += " aad-transition-enter";
              if (isCollapsed) {
                style = {
                  maxWidth: 0,
                  opacity: 0,
                  transform: "scale(0.7)",
                };
              } else {
                style = {
                  maxWidth: charWidth(char),
                };
              }
              break;

            case "exit-delete":
              className += " aad-transition-exit";
              style = {
                maxWidth: 0,
                opacity: 0,
                transform: "scale(0.7)",
              };
              break;

            default:
              break;
          }
        }

        return (
          <span className="aad-slot" key={i}>
            {hasOverlay && (
              <span
                className="aad-char aad-exit-overlay"
                style={{ animation: `aadExitReplace ${EXIT_REPLACE}ms linear forwards` }}
              >
                {exitOverlay.char}
              </span>
            )}
            <span className={className} style={style}>
              {char}
            </span>
          </span>
        );
      })}
      {showCaret && <span className="aad-fake-caret" />}
    </div>
  );
}
